// Imports en haut du fichier
import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

import "./App.css";

// Définition du composant App
const App = () => {
  // États de l'application
  const [date, setDate] = useState(new Date());
  const [startDate, setStartDate] = useState(new Date());
  const [takenDates, setTakenDates] = useState([]);

  // Fonction pour ajouter une prise de médicament dans IndexedDB
  const addToDB = (db, date) => {
    const transaction = db.transaction(["takenDates"], "readwrite");
    const store = transaction.objectStore("takenDates");
    store.add({ date });
  };

  // Fonction pour sauvegarder la date de démarrage dans IndexedDB
  const saveStartDateToDB = (db, startDate) => {
    const transaction = db.transaction(["settings"], "readwrite");
    const store = transaction.objectStore("settings");
    store.put({ key: "startDate", value: startDate });
  };

  // Fonction pour récupérer la date de démarrage depuis IndexedDB
  const getStartDateFromDB = (db) => {
    const transaction = db.transaction(["settings"], "readonly");
    const store = transaction.objectStore("settings");
    const request = store.get("startDate");

    request.onsuccess = () => {
      if (request.result) {
        setStartDate(new Date(request.result.value));
      }
    };
  };

  // Fonction pour supprimer une dose de la base de données
  const deleteFromDB = (db, id) => {
    const transaction = db.transaction(["takenDates"], "readwrite");
    const store = transaction.objectStore("takenDates");
    store.delete(id);
  };

  // Fonction pour marquer une dose comme prise
  const markDoseTaken = async () => {
    const request = indexedDB.open("treatmentDB", 2);

    request.onsuccess = async (e) => {
      const db = e.target.result;
      addToDB(db, date);

      const newTakenDates = [...takenDates, { date, id: Date.now() }];
      newTakenDates.sort((a, b) => new Date(a.date) - new Date(b.date));
      setTakenDates(newTakenDates);
    };

    request.onerror = (e) => {
      console.error("Erreur d'ouverture de la base de données", e);
    };
  };

  // Fonction pour gérer le changement de date de démarrage
  const handleStartDateChange = async (e) => {
    const newStartDate = new Date(e.target.value);
    setStartDate(newStartDate);

    // Sauvegarder dans IndexedDB
    const request = indexedDB.open("treatmentDB", 2);
    request.onsuccess = async (event) => {
      const db = event.target.result;
      saveStartDateToDB(db, newStartDate);
    };
  };

  // Fonction pour supprimer une dose
  const handleDeleteDose = (id) => {
    const request = indexedDB.open("treatmentDB", 2);

    request.onsuccess = (e) => {
      const db = e.target.result;
      deleteFromDB(db, id);

      const updatedTakenDoses = takenDates.filter((item) => item.id !== id);
      setTakenDates(updatedTakenDoses);

      reloadTakenDates(db);
    };

    request.onerror = (e) => {
      console.error("Erreur d'ouverture de la base de données", e);
    };
  };

  // Fonction pour recharger les doses depuis IndexedDB
  const reloadTakenDates = (db) => {
    const transaction = db.transaction(["takenDates"], "readonly");
    const store = transaction.objectStore("takenDates");
    const request = store.getAll();

    request.onsuccess = () => {
      if (request.result) {
        const sortedTakenDates = request.result.sort((a, b) => new Date(a.date) - new Date(b.date));
        setTakenDates(sortedTakenDates);
      }
    };

    request.onerror = () => {
      console.error("Erreur de récupération des données");
    };
  };

  // Fonction pour changer la date sélectionnée dans le calendrier
  const handleDateChange = (newDate) => {
    setDate(newDate);
  };

  // Fonction pour vérifier si une date a une dose prise
  const dateHasDose = (date) => {
    return takenDates.some(dose => {
      const doseDate = new Date(dose.date);
      return doseDate.toDateString() === date.toDateString();
    });
  };

  // Fonction pour personnaliser l'affichage des tuiles du calendrier
  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      if (dateHasDose(date)) {
        return 'has-dose';
      }
    }
    return null;
  };

  // Calculer quelle dose (10IR ou 300IR) en fonction de la date
  const getFlacon = (date) => {
    const treatmentDay = Math.floor((date - startDate) / (1000 * 60 * 60 * 24));
    if (treatmentDay < 6) {
      return (
        <div className="flacon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="50" height="50">
            <circle cx="50" cy="50" r="40" fill="blue" />
            <rect x="40" y="30" width="20" height="40" fill="white" />
            <text x="50" y="50" fontSize="12" textAnchor="middle" fill="white">10IR</text>
          </svg>
          <span>10IR</span>
        </div>
      );
    } else {
      return (
        <div className="flacon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="50" height="50">
            <circle cx="50" cy="50" r="40" fill="pink" />
            <rect x="40" y="30" width="20" height="40" fill="white" />
            <text x="50" y="50" fontSize="12" textAnchor="middle" fill="white">300IR</text>
          </svg>
          <span>300IR</span>
        </div>
      );
    }
  };

  // Calculer la phase actuelle en fonction des jours écoulés
  const getPhase = () => {
    const daysSinceStart = Math.floor((date - startDate) / (1000 * 60 * 60 * 24));
    if (daysSinceStart < 12) {
      return "Phase d'initiation";
    } else {
      return "Phase d'entretien";
    }
  };

  useEffect(() => {
    // Fonction pour initialiser IndexedDB
    const initDB = () => {
      const request = indexedDB.open("treatmentDB", 2);
      request.onupgradeneeded = (e) => {
        const db = e.target.result;

        if (!db.objectStoreNames.contains("takenDates")) {
          db.createObjectStore("takenDates", { keyPath: "id", autoIncrement: true });
        }

        if (!db.objectStoreNames.contains("settings")) {
          db.createObjectStore("settings", { keyPath: "key" });
        }
      };

      request.onerror = (e) => {
        console.error("Erreur d'ouverture IndexedDB", e);
      };

      request.onsuccess = (e) => {
        const db = e.target.result;
        getStoredData(db);
        getStartDateFromDB(db);
      };
    };

    // Fonction pour récupérer les données depuis IndexedDB
    const getStoredData = (db) => {
      const transaction = db.transaction(["takenDates"], "readonly");
      const store = transaction.objectStore("takenDates");
      const request = store.getAll();

      request.onsuccess = () => {
        if (request.result) {
          const sortedTakenDates = request.result.sort((a, b) => new Date(a.date) - new Date(b.date));
          setTakenDates(sortedTakenDates);
        }
      };

      request.onerror = () => {
        console.error("Erreur de récupération des données");
      };
    };

    // Initialiser la base de données
    initDB();

    // Réinitialiser la date à aujourd'hui
    setDate(new Date());
  }, []);

  return (
    <div className="App">
      <h1>Suivi du traitement de désensibilisation</h1>

      <div className="summary">
        <h2>Phase actuelle : {getPhase()}</h2>
        <p>Doses prises : {takenDates.length}</p>
        <p>Jour actuel : {format(date, "dd MMMM yyyy", { locale: fr })}</p>
        <label>
          Date de démarrage du traitement :
          <input
            type="date"
            value={format(startDate, "yyyy-MM-dd")}
            onChange={handleStartDateChange}
          />
        </label>
      </div>

      {/* Affichage du calendrier avec les points pour les doses */}
      <Calendar 
        onChange={handleDateChange} 
        value={date} 
        locale="fr"
        tileClassName={tileClassName}
      />

      {/* Affichage de l'icône du flacon avec le texte à côté */}
      <div className="flacon">
        {getFlacon(date)}
      </div>

      {/* Marquer une prise de médicament */}
      <button className="btn" onClick={markDoseTaken}>Marquer comme prise</button>

      <div className="taken-list">
        <h2>Doses prises :</h2>
        <ul>
          {takenDates.map((dose) => (
            <li key={dose.id}>
              <span>{format(dose.date, "dd MMMM yyyy", { locale: fr })}</span>
              <button 
                className="delete-btn" 
                onClick={() => handleDeleteDose(dose.id)}
              >
                &#10005; {/* Icône croix */}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Footer */}
      <footer className="footer">
        <p>
          Développé par{" "}
          <a href="https://conciergit.fr" target="_blank" rel="noopener noreferrer">
            ConciergIT
          </a>
        </p>
      </footer>
    </div>
  );
};

export default App;
